/**
 * Meta (Facebook) Pixel wrapper.
 *
 * All functions are safe no-ops when REACT_APP_META_PIXEL_ID is unset OR
 * when the fbevents.js script hasn't loaded yet (queued for later flush).
 *
 * Every event carries an `eventID` (crypto UUID) so we can bolt Conversions
 * API onto the backend later and dedupe browser + server events without a
 * frontend rewrite.
 *
 * References:
 * - https://developers.facebook.com/docs/meta-pixel/reference
 * - https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
import { isBotClient } from "@/lib/botGuard";
let initialized = false;
// The inline snippet in public/index.html fires ONE PageView at HTML
// parse time — App.js also fires PageView on every location.pathname
// change (including the very first render). This flag suppresses that
// FIRST duplicate so we don't double-count PageView events.
let firstPageViewSuppressed = false;

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Legacy Safari fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Inject the fbevents.js base pixel snippet once, then call `fbq('init')`.
 * Safe to call multiple times — additional calls are no-ops.
 *
 * Sept 2026 (AddToCart recovery) — the base pixel snippet is now loaded
 * SYNCHRONOUSLY from index.html so `window.fbq` is a queueing stub the
 * moment React starts running. This function has become a defensive
 * fallback for the (rare) case where the inline script was blocked
 * by CSP or a cookie banner. It also fires an SPA PageView once React
 * has hydrated so the first client-side route change isn't lost.
 */
export function initPixel() {
  if (initialized || !PIXEL_ID || typeof window === "undefined") return;
  if (isBotClient()) return;
  initialized = true;

  try {
    // Only inject the base snippet if it hasn't already loaded from
    // index.html (which is the standard code path — this branch is a
    // defensive fallback).
    if (!window.fbq) {
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      // Only init + fire PageView on the fallback path — the inline
      // snippet in index.html already did both. Doing it twice would
      // double-count PageView.
      if (typeof window.fbq === "function") {
        window.fbq("init", PIXEL_ID);
        window.fbq("track", "PageView");
      }
    }
    // If the inline snippet DID load, `window.fbq` is already a stub +
    // fbevents.js is on the wire. Nothing to do here — PageView was
    // fired inline at HTML parse time.
  } catch (err) {
    // Reset flag so a follow-up init attempt (e.g. after page revive) can retry.
    initialized = false;
    // Structured debug — Clarity/PostHog record this, we can search recordings.
    try { console.warn("[meta-pixel] init suppressed", err && err.message); } catch { /* ignore */ }
  }
}

/** P5 — Advanced Matching on the browser pixel. fbevents.js accepts a
 *  second `init` with user_data when the first init carried none (it sets
 *  the pixel's userData + loads the identity plugin — verified against the
 *  fbevents.js source, no DUPLICATE_PIXEL_ID warning in that path). The
 *  pixel hashes plaintext itself. We call this once per page load with the
 *  best identifiers we have so browser Purchase / ATC events carry the
 *  same match keys as the CAPI mirror. */
let advancedMatchingSet = false;

function normalisePhone(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.startsWith("0") && (d.length === 9 || d.length === 10)) d = "61" + d.slice(1);
  else if (d.length === 9 && d.startsWith("4")) d = "61" + d;
  return d;
}

export function setAdvancedMatching(ids) {
  if (advancedMatchingSet || !PIXEL_ID || typeof window === "undefined" || !window.fbq) return false;
  const src = ids || {};
  const ud = {};
  if (src.em) ud.em = String(src.em).trim().toLowerCase();
  if (src.ph) { const p = normalisePhone(src.ph); if (p) ud.ph = p; }
  if (src.fn) ud.fn = String(src.fn).trim().toLowerCase();
  if (src.ln) ud.ln = String(src.ln).trim().toLowerCase();
  if (src.ct) ud.ct = String(src.ct).trim().toLowerCase().replace(/[^a-z]/g, "");
  if (src.st) ud.st = String(src.st).trim().toLowerCase().slice(0, 3);
  if (src.zp) ud.zp = String(src.zp).trim().toLowerCase();
  if (src.country) ud.country = String(src.country).trim().toLowerCase().slice(0, 2);
  if (src.external_id) ud.external_id = String(src.external_id);
  // Only worth it once we hold a strong key — email or phone.
  if (!ud.em && !ud.ph) return false;
  try {
    window.fbq("init", PIXEL_ID, ud);
    advancedMatchingSet = true;
    return true;
  } catch (err) {
    console.warn("[fbq] advanced matching", err);
    return false;
  }
}

/**
 * Fire an event by name. `standard=true` uses `fbq('track', ...)` for Meta's
 * predefined events; `standard=false` uses `fbq('trackCustom', ...)` for
 * anything custom.
 *
 * Also mirrors the event to our backend `/api/meta/events` endpoint using
 * the same `eventID` so Meta dedupes browser + server events (fixes the
 * iOS 14.5+ / ad-blocker attribution gap without inflating totals).
 */
export function trackEvent(eventName, params = {}, { standard = true, eventId } = {}) {
  if (!PIXEL_ID || typeof window === "undefined") return null;
  // P4 — crawlers / webdriver clients never fire pixel or CAPI events.
  if (isBotClient()) return null;
  // P5 — attach advanced matching before the first identified event.
  if (!advancedMatchingSet && window.fbq) {
    try { setAdvancedMatching(collectUserIdentifiers()); } catch { /* silent */ }
  }
  // Callers can pass a deterministic `eventId` (e.g. `purchase_{order_id}`)
  // so browser + server events for the same real-world action dedupe
  // against each other on Meta's side. Falls back to a random UUID for
  // events that don't need cross-source dedup (PageView, ViewContent).
  const eventID = eventId || uuid();
  if (window.fbq) {
    const method = standard ? "track" : "trackCustom";
    try {
      window.fbq(method, eventName, params, { eventID });
    } catch (err) {
      console.warn("[fbq]", eventName, err);
    }
  }
  // Server-side CAPI mirror — fire-and-forget, never blocks UX.
  try {
    mirrorToServer(eventName, eventID, params);
  } catch { /* swallow */ }
  return eventID;
}

/** Read Meta browser identifiers (fbp / fbc cookies) so server-side events
 *  can be matched against the same visitor's browser events. Exported so
 *  the checkout payload can carry them to the Stripe-webhook Purchase (P5). */
export function readMetaCookies() {
  if (typeof document === "undefined") return {};
  const out = {};
  const pairs = document.cookie.split(";");
  for (const p of pairs) {
    const [k, v] = p.trim().split("=");
    if (k === "_fbp" && v) out.fbp = v;
    if (k === "_fbc" && v) out.fbc = v;
  }
  // Synthesise `_fbc` from a `?fbclid=` URL parameter if the cookie is
  // missing — Meta's spec is `fb.1.{timestamp_ms}.{fbclid}`. This closes
  // the "click just landed, cookie hasn't been persisted yet" gap so the
  // very first PageView / ViewContent still carries the click id.
  if (!out.fbc && typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get("fbclid");
      if (fbclid) out.fbc = `fb.1.${Date.now()}.${fbclid}`;
    } catch { /* URL parse failure — ignore */ }
  }
  return out;
}

/** Collect every user identifier available RIGHT NOW so CAPI events
 *  carry match-quality signals. Meta's Diagnostics tab flags any event
 *  sent without user_data — the browser pixel supplies match keys via
 *  cookies + UA fingerprinting automatically but the CAPI mirror needs
 *  us to pass them explicitly.
 *
 *  Sources (in priority order, first non-empty wins):
 *   - email:    localStorage `tbb_capture_email` (set by delivery-checker
 *               popup, cart address form, login flow)
 *   - name:     `tbb_capture_name` split into fn/ln
 *   - phone:    `tbb_capture_phone` (set by cart address form)
 *   - postcode: `tbb_delivery_postcode` (set by delivery-checker + cart)
 *   - external_id: `tbb_user_id` (set on login/logout)
 *
 *  We NEVER hash anything client-side — the backend does that (see
 *  `meta_capi._normalize_user_data`) so the hashing is centralised and
 *  we can't accidentally leak plaintext to Meta if the frontend has
 *  a bug.
 */
function collectUserIdentifiers() {
  if (typeof window === "undefined") return {};
  const out = {};
  try {
    const email = (localStorage.getItem("tbb_capture_email") || "").trim();
    if (email && email.includes("@")) out.em = email;

    const name = (localStorage.getItem("tbb_capture_name") || "").trim();
    if (name) {
      const parts = name.split(/\s+/);
      out.fn = parts[0];
      if (parts.length > 1) out.ln = parts.slice(1).join(" ");
    }

    const phone = (localStorage.getItem("tbb_capture_phone") || "").trim();
    if (phone) out.ph = phone;

    const postcode = (localStorage.getItem("tbb_delivery_postcode") || "").trim();
    if (postcode) {
      out.zp = postcode;
      // Every WA postcode we serve is metro/regional Perth — safe to stamp
      // country + state so Meta's match rate improves without leaking any
      // PII we don't already have.
      out.country = "au";
      out.st = "wa";
    }

    // Suburb / city, captured by either the delivery-checker waitlist
    // form OR the cart address form. Meta hashes this via `ct` — the
    // extra match key typically bumps composite EMQ score by 0.3-0.5
    // when combined with `zp`.
    const city = (localStorage.getItem("tbb_capture_city") || "").trim();
    if (city) out.ct = city;

    const userId = (localStorage.getItem("tbb_user_id") || "").trim();
    if (userId) out.external_id = userId;
  } catch { /* localStorage disabled (private mode etc.) — return what we have */ }
  return out;
}

/** Fire the same event server-side via /api/meta/events. Only for
 *  browser-allowed events (Purchase is minted from the Stripe webhook). */
async function mirrorToServer(eventName, eventID, params) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  if (!BACKEND) return;
  const ALLOWED = new Set(["PageView", "ViewContent", "AddToCart", "InitiateCheckout"]);
  if (!ALLOWED.has(eventName)) return;
  const { fbp, fbc } = readMetaCookies();
  const ids = collectUserIdentifiers();
  const body = {
    event_name: eventName,
    event_id: eventID,
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    fbp, fbc,
    // Match-quality identifiers — server hashes the PII per Meta spec.
    // Sending as much as we have RIGHT NOW closes the "AddToCart sent
    // without user_data" Diagnostics warning that Meta raises when
    // events arrive with only fbp/fbc.
    em: ids.em,
    ph: ids.ph,
    fn: ids.fn,
    ln: ids.ln,
    zp: ids.zp,
    ct: ids.ct,
    st: ids.st,
    country: ids.country,
    external_id: ids.external_id,
    custom_data: params || {},
  };
  // Use sendBeacon on unload paths (InitiateCheckout right before nav) so
  // the request survives navigation; otherwise plain fetch.
  const url = `${BACKEND}/api/meta/events`;
  try {
    if (navigator.sendBeacon && eventName === "InitiateCheckout") {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
}

// -------------------------------------------------------------
// Standard eCommerce events
// -------------------------------------------------------------

export function trackPageView() {
  if (!firstPageViewSuppressed) {
    // The inline snippet in public/index.html already fired the FIRST
    // PageView at HTML parse time — dropping this one prevents a
    // duplicate on cold loads. Every subsequent SPA route change
    // still fires normally.
    firstPageViewSuppressed = true;
    return null;
  }
  return trackEvent("PageView");
}

/**
 * @param {object} item  { slug, name, price, kind }
 */
export function trackViewContent(item) {
  if (!item) return null;
  return trackEvent("ViewContent", {
    content_ids: [item.slug],
    content_name: item.name,
    content_type: item.kind === "box" ? "product_group" : "product",
    content_category: item.category,
    value: Number(item.price) || 0,
    currency: "AUD",
  });
}

export function trackAddToCart(item) {
  if (!item) return null;
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  return trackEvent("AddToCart", {
    content_ids: [item.slug],
    content_name: item.name,
    content_type: item.kind === "box" ? "product_group" : "product",
    value,
    currency: "AUD",
  });
}

/**
 * @param {object} cart  { items: [...], total: number, subscription: boolean }
 */
export function trackInitiateCheckout(cart) {
  const items = cart?.items || [];
  return trackEvent("InitiateCheckout", {
    content_ids: items.map((i) => i.slug),
    num_items: items.reduce((n, i) => n + (i.quantity || 0), 0),
    value: Number(cart?.total) || 0,
    currency: "AUD",
    contents: items.map((i) => ({
      id: i.slug,
      quantity: i.quantity || 1,
      item_price: i.price || 0,
    })),
  });
}

/**
 * @param {object} order  { order_id, amount, items?, subscription? }
 * Fires Purchase always. If subscription=true, also fires Subscribe with the
 * MRR value so Meta learns high-LTV signals.
 */
export function trackPurchase(order) {
  if (!order) return null;
  // P5 — the success page hands us the order's own identifiers (em/ph/
  // fn/ln/ct/st/zp/country/external_id) so the browser Purchase matches
  // as well as the CAPI one.
  if (order.match) {
    try { setAdvancedMatching(order.match); } catch { /* silent */ }
  }
  const value = Number(order.amount) || 0;
  const items = order.items || [];
  const params = {
    value,
    currency: "AUD",
    content_ids: items.map((i) => i.slug),
    content_type: "product",
    contents: items.map((i) => ({
      id: i.slug,
      quantity: i.quantity || 1,
      item_price: i.price || 0,
    })),
    num_items: items.reduce((n, i) => n + (i.quantity || 0), 0),
    order_id: order.order_id,
  };
  // Deterministic event_id so browser + Stripe-webhook Purchase events
  // dedupe on Meta's side. If a shopper never reaches /checkout/success
  // (closed tab, network drop, redirect fail), the webhook Purchase
  // still fires with the same id — Meta records exactly 1 conversion
  // either way. Sept 2026 fix — see docstring in
  // `backend/routers/checkout.py::_fire_meta_purchase_from_webhook`.
  const deterministicEventId = order.order_id ? `purchase_${order.order_id}` : undefined;
  const purchaseId = trackEvent("Purchase", params, { eventId: deterministicEventId });
  // Server-side echo — Meta dedupes on `event_id`. Backend reads authoritative
  // amount/items from the order in the DB. Idempotent — the webhook Purchase
  // has probably already fired and set meta_purchase_sent=True, in which
  // case this endpoint returns {skipped: true, reason: "already_sent"}.
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  if (BACKEND && order.order_id && purchaseId) {
    try {
      fetch(`${BACKEND}/api/meta/events/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: order.order_id, event_id: purchaseId }),
        credentials: "include",
        keepalive: true,
      }).catch(() => {});
    } catch { /* silent */ }
  }
  if (order.subscription) {
    trackEvent("Subscribe", { value, currency: "AUD", predicted_ltv: value * 6 });
  }
  return purchaseId;
}

/** Fired when a visitor submits the "$20 off" newsletter popup. */
export function trackLead(email) {
  return trackEvent("Lead", {
    content_name: "welcome-popup",
    // Do NOT send raw email in browser events — Meta requires hashed PII.
    // Left here as a hook for future CAPI implementation (hash server-side).
    lead_event_source: "email-capture-popup",
    ...(email ? {} : {}),
  });
}

export function trackCompleteRegistration(method = "email") {
  return trackEvent("CompleteRegistration", { registration_method: method });
}
